import { useEffect, useRef, useState } from "react";
import {
  fetchAuthenticatedFileBlob,
  isProtectedApiFileUrl,
} from "../utils/authenticatedFileUrl";

export function useAuthenticatedFileUrl(
  sourceUrl: string | undefined | null,
): {
  url: string | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const [url, setUrl] = useState<string | undefined>(() => {
    if (!sourceUrl) return undefined;
    return isProtectedApiFileUrl(sourceUrl) ? undefined : sourceUrl;
  });
  const [isLoading, setIsLoading] = useState(
    () => !!sourceUrl && isProtectedApiFileUrl(sourceUrl),
  );
  const [error, setError] = useState<Error | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sourceUrl) {
      setUrl(undefined);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!isProtectedApiFileUrl(sourceUrl)) {
      setUrl(sourceUrl);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setUrl(undefined);

    fetchAuthenticatedFileBlob(sourceUrl)
      .then((blob) => {
        if (cancelled) return;

        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }

        const objectUrl = URL.createObjectURL(blob);
        blobUrlRef.current = objectUrl;
        setUrl(objectUrl);
        setIsLoading(false);
      })
      .catch((fetchError) => {
        if (cancelled) return;

        setError(
          fetchError instanceof Error
            ? fetchError
            : new Error(String(fetchError)),
        );
        setUrl(undefined);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sourceUrl]);

  useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    },
    [],
  );

  return { url, isLoading, error };
}
